import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik, FormikProvider } from 'formik'
import * as yup from 'yup'
import moment from 'moment'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import {
  POST_CREATE_CONTRACTOR_KEY,
  postCreateContractor,
} from '../payrollActions'
import { getContractorName } from '../helpers'
import { SubscriptionIncrease } from './SubscriptionIncrease'
import { PayrollError } from '../PayrollError'
import { Text, Button, Form, Dialog, Card, Alert, Icon, Separator } from '@/ui'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import {
  makeReqStringSchema,
  makeNumberSchema,
  makeDateSchema,
  FormikRadioButton,
  getFieldName,
  FormikLabelError,
  makeReqEmailSchema,
} from '@/componentsV1/BaseComponents'
const END_VAL = 'for this contractor'
const validationSchema = yup.object({
  first_name: yup
    .string()
    .when('type', ([type], schema) =>
      type === 'Individual'
        ? makeReqStringSchema({ field: 'first name', end: END_VAL })
        : schema
    ),
  last_name: yup
    .string()
    .when('type', ([type], schema) =>
      type === 'Individual'
        ? makeReqStringSchema({ field: 'last name', end: END_VAL })
        : schema
    ),
  email: makeReqEmailSchema({ field: 'email', end: END_VAL }),
  business_name: yup
    .string()
    .when('type', ([type], schema) =>
      type === 'Business'
        ? makeReqStringSchema({ field: 'business name', end: END_VAL })
        : schema
    ),
  ein: yup.string().when('type', ([type], schema) =>
    type === 'Business'
      ? makeNumberSchema({
          field: 'EIN',
          numDigits: 9,
          end: END_VAL,
          allowLeadingZero: true,
        })
      : schema
  ),
  hourly_rate: yup.string().when('wage_type', ([wage_type], schema) =>
    wage_type === 'Hourly'
      ? makeNumberSchema({
          field: 'hourly rate',
          end: END_VAL,
          allowedDecimals: 2,
        })
      : schema
  ),
  start_date: makeDateSchema({ field: 'start date', end: END_VAL }),
  type: makeReqStringSchema({ field: 'type', end: END_VAL }),
  wage_type: makeReqStringSchema({ field: 'wage type', end: END_VAL }),
})

export const CreateContractor = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)

  const formik = useFormik({
    initialValues: {
      type: undefined as 'Individual' | 'Business' | undefined,
      first_name: '',
      last_name: '',
      business_name: '',
      email: '',
      ein: '',
      hourly_rate: '',
      wage_type: undefined as 'Hourly' | 'Fixed' | undefined,
      self_onboarding: true,
      start_date: '',
    },
    validationSchema,
    onSubmit: () => setModalOpen(true),
  })

  const { values, submitForm } = formik

  const onSave = useCallback(async () => {
    if (!values.type || !values.wage_type) {
      return
    }

    const res = await postCreateContractor({
      ...values,
      type: values.type,
      wage_type: values.wage_type,
      start_date: moment(values.start_date).format(DATE_FORMATS.GUSTO_SUBMIT),
    })(dispatch)

    if (res) {
      navigate(`/payroll/contractor/${res.uuid}`)
    }
  }, [dispatch, values, navigate])

  const contractorName = getContractorName({
    first_name: values.first_name,
    last_name: values.last_name,
    type: values.type,
    business_name: values.business_name,
  })

  return (
    <Card>
      <FormikProvider value={formik}>
        <div className="flex flex-col gap-6">
          <Text variant={TypographyStyles.H1}>Add a new contractor</Text>

          <Alert
            variant="default"
            description="You can invite contractors to enter their information online, or you can do it yourself. Self-onboarding saves time and lets contractors access their payments and yearly tax forms online."
          />

          <Text variant={TypographyStyles.H2}>
            Let&apos;s onboard your contractor!
          </Text>
          <Separator />

          <FormikLabelError
            name={getFieldName<typeof values>('type')}
            label="Contractor Type"
            required
          />

          <div className="flex gap-4">
            <FormikRadioButton
              name={getFieldName<typeof values>('type')}
              value="Individual"
              label="Individual"
            />
            <FormikRadioButton
              name={getFieldName<typeof values>('type')}
              value="Business"
              label="Business"
            />
          </div>

          {values.type === 'Individual' && (
            <div className="flex flex-wrap gap-4">
              <div className="w-full md:w-1/2">
                <Form.Input
                  name={getFieldName<typeof values>('first_name')}
                  label="First name"
                  description="Their official, legal name."
                  required
                />
              </div>
              <div className="w-full md:w-1/2">
                <Form.Input
                  name={getFieldName<typeof values>('last_name')}
                  label="Last name"
                  description="Their official, legal name."
                  required
                />
              </div>
            </div>
          )}

          {values.type === 'Business' && (
            <>
              <Form.Input
                name={getFieldName<typeof values>('business_name')}
                label="Business Name"
                required
                description="The contractor's business name is needed to file their annual 1099 tax form."
              />
              <Form.Input
                name={getFieldName<typeof values>('ein')}
                label="EIN (Employer Identification Number)"
                required
              />
            </>
          )}

          {values.type && (
            <div className="w-full md:w-1/2">
              <Form.DateInput
                name={getFieldName<typeof values>('start_date')}
                label="Start Date"
                description="Their first day of work"
                required
              />
            </div>
          )}

          <Text variant={TypographyStyles.H2}>Compensation</Text>
          <Separator />

          <FormikLabelError
            name={getFieldName<typeof values>('wage_type')}
            label="Wage Type"
            description="We can calculate the total pay when you enter this contractor's hours, or you can enter an exact dollar amount each time you pay them."
            required
          />

          <div className="flex gap-4">
            <FormikRadioButton
              name={getFieldName<typeof values>('wage_type')}
              value="Hourly"
              label="Hourly Rate"
            />
            <FormikRadioButton
              name={getFieldName<typeof values>('wage_type')}
              value="Fixed"
              label="Fixed Dollar Amount"
            />
          </div>

          {values.wage_type === 'Hourly' && (
            <Form.Input
              name={getFieldName<typeof values>('hourly_rate')}
              label="Hourly Rate"
              description="You can change this any time"
              componentType="currency"
            />
          )}

          <Text variant={TypographyStyles.H2}>Contractor Access</Text>
          <Separator />

          <Text variant={TypographyStyles.BODY_MD}>
            We recommend inviting your contractor to Gusto. This allows them to
            view their payments and download their annual 1099 tax form.
          </Text>

          <Form.Input
            name={getFieldName<typeof values>('email')}
            type="email"
            label="Email"
            required
          />

          <div className="flex flex-wrap gap-4 mt-4">
            <div className="w-full md:w-1/4">
              <Button
                variant="secondary"
                onClick={() => navigate('/payroll/employees')}
              >
                Cancel
              </Button>
            </div>
            <div className="flex-grow" />
            <div className="w-full md:w-1/3">
              <Button onClick={submitForm}>
                Submit
                <Icon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </FormikProvider>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <Dialog.Content size="sm">
          <Dialog.Title>Confirm New Contractor: {contractorName}</Dialog.Title>

          <div className="flex flex-col gap-4 mt-4">
            <PayrollError fetchKey={POST_CREATE_CONTRACTOR_KEY} />

            <Text variant={TypographyStyles.BODY_MD}>
              You are about to add a new member to your team! Just confirming a
              couple of details.
            </Text>

            <Text variant={TypographyStyles.BODY_MD}>Name</Text>
            <Text>{contractorName}</Text>

            <Text variant={TypographyStyles.BODY_MD}>Compensation</Text>
            <Text>
              {values.wage_type === 'Hourly'
                ? `$${values.hourly_rate} per hour`
                : 'Fixed'}
            </Text>
          </div>

          <div className="price-increase-wrapper text-green-700">
            <Text variant={TypographyStyles.BODY_MD}>Price increase</Text>
            <Text variant={TypographyStyles.BODY_MD}>
              <SubscriptionIncrease />
            </Text>
          </div>

          <Dialog.Footer className="pt-4 flex justify-end gap-2">
            <Button onClick={() => setModalOpen(false)} variant="secondary">
              Cancel
            </Button>
            <Button onClick={onSave}>Save</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </Card>
  )
}
